import { useSearchParams } from "react-router-dom";
import type { ViewProps } from "./OverviewView";
import { useWebsiteReport } from "../useAnalyticsContext";
import type { Overview, Timeseries } from "../types";
import MetricGrid from "../components/MetricGrid";
import TrendChart from "../components/TrendChart";
import ReportTable from "../components/ReportTable";
import { pageColumns, searchColumns, type ReportColumn } from "../tableConfig";
import { ErrorState, Loading, MissingSource } from "../components/States";
import { rangeParams } from "../dateRange";
import styles from "../Analytics.module.css";
import { sourceSelection } from "../reportSelection";
export default function SourceReportView(
  props: ViewProps & { source: "ga4" | "gsc" },
) {
  const { scope, id, range, context, source } = props;
  const [params, setParams] = useSearchParams();
  const isSearch = source === "gsc";
  const breakdowns = context.sources[source].available_breakdowns ?? [];
  const { metrics, metric, view, options, report } = sourceSelection(
    source,
    params,
    breakdowns,
  );
  const available =
    context.sources[source].connection_status !== "not_connected";
  const overview = useWebsiteReport<Overview>(
    scope,
    id,
    range,
    context,
    "overview",
    {},
    available,
  );
  const trend = useWebsiteReport<Timeseries>(
    scope,
    id,
    range,
    context,
    "timeseries",
    { source, metric },
    available,
  );
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    next.delete("page");
    next.delete("pageKey");
    next.delete("q");
    next.delete("sort");
    setParams(next);
  };
  let columns: ReportColumn[] = isSearch
    ? searchColumns
    : pageColumns.filter((c) =>
        ["page", "sessions", "pageviews"].includes(c.key),
      );
  if (view === "pages" && isSearch)
    columns = pageColumns.filter((c) =>
      ["page", "clicks", "impressions", "ctr", "position"].includes(c.key),
    );
  if (view === "countries" || view === "devices")
    columns = [
      {
        key: view === "countries" ? "country" : "device",
        label: view === "countries" ? "Country" : "Device",
        mobile: true,
      },
      ...columns.filter((c) => !["query", "page"].includes(c.key)),
    ];
  return !available ? (
    <MissingSource
      source={source}
      href={`/analytics/websites/${id}/connections?${rangeParams(range)}`}
    />
  ) : (
    <div className={styles.stack}>
      {overview.isPending ? (
        <Loading />
      ) : overview.isError ? (
        <ErrorState error={overview.error} retry={() => overview.refetch()} />
      ) : (
        <MetricGrid
          metrics={overview.data.payload.metrics}
          names={metrics}
          compare={range.compare === "previous"}
          selected={metric}
          onSelect={(name) => update("metric", name)}
        />
      )}
      {trend.isPending ? (
        <Loading label="Loading trend…" />
      ) : trend.isError ? (
        <ErrorState error={trend.error} retry={() => trend.refetch()} />
      ) : (
        <TrendChart
          data={trend.data.payload}
          compare={range.compare === "previous"}
        />
      )}
      <div className={styles.segmented} aria-label="Report breakdown">
        {options.map((option) => (
          <button
            key={option.id}
            aria-pressed={view === option.id}
            onClick={() => update("view", option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <ReportTable
        {...props}
        report={report}
        title={options.find((o) => o.id === view)!.label}
        columns={columns}
      />
      {!isSearch && !breakdowns.some((v) => v.includes("device")) && (
        <p className={styles.caption}>
          Device data has not been collected for this connection.
        </p>
      )}
    </div>
  );
}
